import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';

import {
  KocAilemContainer,
  SearchWrapper,
  SearchWrapperContent,
  StickyLogoBox,
} from '@components/views/hotel/listing/HotelListingStyle';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import StickyElement from '@components/utils/StickyElement';
import { getRoomsFromCookie } from '@components/views/landing/components/bookerNext/Booker';
import { useSearchURL } from '@api/data-loaders/resource';
import { getLoyaltyTypeFromUtmSource } from '@components/ui/loyalty-verifications/helpers';
import { useDevice } from '@contexts/device';
import SearchReservedId from '@components/ui/SearchReservedId';
import { useKocAliemContext } from '@contexts/loyalty/KocAilem';
import { convertQueryRoomToHotelRooms } from '@components/views/hotel/utils';
import { paramsToObject } from '@components/views/utils';

import SearchBar from '../../detail/components/package-search-bar/SearchBar';

const KocAilemBanner = dynamic(
  () =>
    import('@components/views/hotel/listing/components/KocAilem/KocAilemBanner')
);

interface Props {
  isSearchedFromSearchBar: Partial<boolean>;
  setIsSearchedFromSearchBar: React.Dispatch<
    React.SetStateAction<Partial<boolean>>
  >;
  setIsFetchedItems: React.Dispatch<React.SetStateAction<boolean>>;
  shouldShallowPush?: boolean;
  isListing?: boolean;
  packageTabChanged?: boolean;
  setPackageTabChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  isAirportSelectionClicked?: boolean;
  setIsAirportSelectionClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  isAirportDateSelectionStarted?: boolean;
  setisAirportDateSelectionStarted?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
  isHoliday?: boolean;
  isPackage?: boolean;
}

const PackageSearchBar = ({
  setIsSearchedFromSearchBar,
  setIsFetchedItems,
  shouldShallowPush,
  isListing,
  packageTabChanged,
  setPackageTabChanged,
  isAirportSelectionClicked,
  setIsAirportSelectionClicked,
  isAirportDateSelectionStarted,
  setisAirportDateSelectionStarted,
  setIsRedirecting,
  isHoliday,
}: Props) => {
  const {
    location,
    hotelRoomsList,
    startDate,
    endDate,
    setDates,
    setLocation,
    setIsStickySearch,
    isStickySearch,
    setHotelRoomsList,
    isLocationEntered,
    setIsLocationEntered,
    setHotelDateSelection,
    isHotelDateSelected,
  } = useSearchBarContext();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { isDesktop } = useDevice();
  const loyaltyType = getLoyaltyTypeFromUtmSource(query?.utm_source as string);

  const [hotelRooms, setHotelRooms] = useState(getRoomsFromCookie);

  const { text: searchText } = useSearchURL();
  const [locationInputVal, setLocationInputVal] = useState<string>(searchText);
  const { isValidated: isKocAilemValidate } = useKocAliemContext();

  useEffect(() => {
    const rooms = convertQueryRoomToHotelRooms(query.room as string);

    if (rooms?.length > 0) {
      setHotelRoomsList(rooms);
      setHotelRooms(rooms);
    }
  }, [query.room, setHotelRoomsList]);

  if (isDesktop === undefined) return null;
  return (
    <SearchWrapper sticky={isStickySearch}>
      <SearchReservedId show={isStickySearch} />
      <StickyElement onStickyStateChange={setIsStickySearch} max={400} />
      <SearchWrapperContent sticky={isStickySearch}>
        {isStickySearch ? (
          <StickyLogoBox>
            <Link href='/' prefetch={false}>
              <Image
                alt='Setur Logo'
                src='/static/images/misc-setur-logo-s.svg'
                width={22}
                height={30}
              />
            </Link>
          </StickyLogoBox>
        ) : null}

        <SearchBar
          setIsRedirecting={setIsRedirecting}
          location={location}
          setDates={setDates}
          startDate={startDate}
          endDate={endDate}
          hotelRoomsList={hotelRoomsList}
          setHotelRoomsList={setHotelRoomsList}
          initialHotelRooms={hotelRooms}
          setInitialHotelRooms={setHotelRooms}
          isLocationEntered={isLocationEntered}
          setIsLocationEntered={setIsLocationEntered}
          setIsFetchedItems={setIsFetchedItems}
          isStickySearch={isStickySearch}
          locationInputVal={locationInputVal}
          setLocation={setLocation}
          setLocationInputVal={setLocationInputVal}
          isDateSelected={isHotelDateSelected}
          setIsDateSelected={setHotelDateSelection}
          onResearchClick={() => {
            setIsSearchedFromSearchBar(true);
            setPackageTabChanged?.(false);
          }}
          shouldShallowPush={shouldShallowPush}
          isHotelListing={isListing}
          packageTabChanged={packageTabChanged}
          isAirportSelectionClicked={isAirportSelectionClicked}
          setIsAirportSelectionClicked={setIsAirportSelectionClicked}
          isAirportDateSelectionStarted={isAirportDateSelectionStarted}
          setisAirportDateSelectionStarted={setisAirportDateSelectionStarted}
          isHoliday={isHoliday}
        />
      </SearchWrapperContent>
      {loyaltyType && isStickySearch && !isKocAilemValidate ? (
        <KocAilemContainer>
          <KocAilemBanner hotelListing={true} />
        </KocAilemContainer>
      ) : null}
    </SearchWrapper>
  );
};

export default PackageSearchBar;
